import { IntlNumberFormat, Locale } from 'vue-i18n'
import {
  NumberFormats,
  SpecificNumberFormatOptions,
  CurrencyNumberFormatOptions,
  NumberFormatOptions,
} from '@intlify/core-base'

import { CurrencySymbol, CURRENCY_FALLBACK } from '@/popup/lib/currencies'

export type Formatters =
  | 'currency'
  | 'decimal'
  | 'percent'
  | 'short'
  | 'token'
  | 'lyx'
  | 'compact'

type FormatsSchema = { [key in Formatters]: NumberFormatOptions }

/**
 * This is number formats schema. It's generated for each locale.
 */
const formatsSchema: FormatsSchema = {
  currency: {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  } as CurrencyNumberFormatOptions,
  decimal: {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  } as SpecificNumberFormatOptions,
  short: {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  } as SpecificNumberFormatOptions,
  percent: {
    style: 'percent',
    useGrouping: false,
  } as SpecificNumberFormatOptions,
  token: {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  } as SpecificNumberFormatOptions,
  lyx: {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  } as SpecificNumberFormatOptions,
  compact: {
    style: 'decimal',
    notation: 'compact',
  } as SpecificNumberFormatOptions,
}

/**
 * Generate number formats from the schema.
 * It updates currency symbol for currency format.
 *
 * @param currencySymbol - Currency symbol
 * @returns - Number formats
 */
export function generateNumberFormat(
  currencySymbol?: CurrencySymbol
): IntlNumberFormat {
  formatsSchema.currency.currency = currencySymbol || CURRENCY_FALLBACK
  return formatsSchema
}

/**
 * Generate number formats for the locale.
 *
 * @param locale - The locale to generate formats for
 * @param currencySymbol - The currency symbol to use for currency format
 * @returns - Number formats
 */
export const numberFormats = (
  locale: Locale,
  currencySymbol?: CurrencySymbol
): NumberFormats => {
  const formats: NumberFormats = {}

  formats[locale] = generateNumberFormat(currencySymbol)

  return formats
}
